import { BadgeText, Flex, Icon, Spacer } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { useResolve, useResolveController, withNewContainer } from '@visiba/instantiation';
import { Async } from '@component/async.component';
import { Title } from '@component/title.component';
import { Location, ScreenLocationController } from './screen_location.controller';
import { ActionCard } from '@component/action_card.component';
import { Dayjs } from 'dayjs';
import { DateService } from '@application/time/date.service';
import { withTranslation } from '@application/i18n/with_translation';
import { EmptyState } from '@application/flexible_component/flexible_identifier/flexible_identifier_flexible_recent_visits.component';
import { Shimmer } from '@component/new_shimmer.component';
import { capitalizeFirstLetter } from '@module/capitalize_first_letter';
import { TopBar } from '@component/top_bar.component';
import { ViewBookingAnalyticActions } from '../view_booking.controller';
import { useAnalyticContext } from '@application/analytic/analytic_link/mod';

export const ScreenLocation = withNewContainer(function ScreenLocation() {
	const controller = useResolveController(ScreenLocationController);
	const analytic = useAnalyticContext();
	const dateService = useResolve(DateService);

	function formatNextAvailableTime(date: Dayjs): string {
		let dateFormat = '';
		const timeFormat = date.format('HH:mm');

		if (dateService.isToday(date)) {
			dateFormat = withTranslation('Common.Word.Today');
		} else if (dateService.isTomorrow(date)) {
			dateFormat = withTranslation('Common.Word.Tomorrow');
		} else if (dateService.isThisYear(date)) {
			dateFormat = date.format('dddd D MMMM');
		} else {
			dateFormat = date.format('LL');
		}

		return withTranslation('View.Booking.ScreenLocation.NextAvailableTime', {
			date: capitalizeFirstLetter(dateFormat),
			time: timeFormat,
		});
	}

	function handleLocation(location: Location) {
		return () => {
			analytic.trackEvent(ViewBookingAnalyticActions.SelectLocation, `${location.id}`);
			controller.selectLocation(location);
		};
	}

	return (
		<>
			<TopBar.Content goBack />
			<Title
				title={withTranslation('View.Booking.ScreenLocation.Title')}
				titleSize='small'
				description={withTranslation('View.Booking.ScreenLocation.Description')}
			/>

			<Spacer vertical={tokens.spacing.x2_5} />

			<Flex gap={tokens.spacing.x1}>
				<Async derive={controller.locations} fallback={<Skeleton />} catch='Blä nu giock det fel...'>
					{(locations) => (
						<>
							{locations.length ? (
								<>
									{locations
										.filter((location) => location.nextAvailableTime)
										.map((location) => (
											<ActionCard.Root
												key={location.id}
												icon={<Icon type={location.type === 'digital' ? 'video' : 'intoBuilding'} size='large' />}
												iconAlign='center'
												onClick={handleLocation(location)}
												variant='slim'
											>
												<ActionCard.Content>
													<ActionCard.Title>{location.name}</ActionCard.Title>
													<ActionCard.Meta>{location.meta}</ActionCard.Meta>
													{location.nextAvailableTime ? (
														<BadgeText variant='brandLight'>{formatNextAvailableTime(location.nextAvailableTime)}</BadgeText>
													) : null}
												</ActionCard.Content>
											</ActionCard.Root>
										))}
								</>
							) : (
								<EmptyState>{withTranslation('View.Booking.ScreenLocation.NoAvailableTimes')}</EmptyState>
							)}
						</>
					)}
				</Async>
			</Flex>
		</>
	);
});

function Skeleton() {
	return (
		<>
			<Shimmer height={80} width='100%' />
			<Shimmer height={80} width='100%' />
			<Shimmer height={80} width='100%' />
		</>
	);
}
